import React, { useEffect, useState } from "react"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Loader2 } from "lucide-react"
import { useToast } from "src/components/ui/toast"
import { fetchFunnel, exportUrl } from "src/features/analytics/api/analyticsApi"
import { useReport } from "src/features/analytics/hooks/useReport"
import { presetToRange, formatNumber } from "src/features/analytics/constants"
import ReportFilters from "src/features/analytics/components/ReportFilters"
import KpiCard from "src/features/analytics/components/KpiCard"
import FunnelChart from "src/features/analytics/components/FunnelChart"
import ConversionTable from "src/features/analytics/components/ConversionTable"
import EmptyReportState from "src/features/analytics/components/EmptyReportState"

export default function FunnelReportPage() {
  const { toast } = useToast()
  const [preset, setPreset] = useState("30d")
  const [filters, setFilters] = useState(presetToRange("30d"))
  const [by, setBy] = useState("")
  const { data, loading, fetch } = useReport(fetchFunnel)

  useEffect(() => {
    fetch({ ...filters, by }).catch((err) => {
      if (err?.response?.status === 403) {
        toast({ variant: "destructive", title: "Error", description: "You don't have permission to view reports." })
      }
    })
  }, [filters.from, filters.to, filters.project_id, filters.pipeline_id, by])

  const overall = data?.overall

  return (
    <div className="mx-auto max-w-[1120px]">
      <ReportFilters
        filters={filters}
        onFiltersChange={setFilters}
        preset={preset}
        onPresetChange={setPreset}
        exportUrl={exportUrl("funnel", { ...filters, by })}
        showByFilter
        byValue={by}
        onByChange={setBy}
      />

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !data?.stages?.length ? (
        <EmptyReportState message="No deals found in this period. Create deals to see your funnel." />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-3 gap-3">
            <KpiCard label="Entered" value={formatNumber(overall?.entered)} />
            <KpiCard label="Won" value={formatNumber(overall?.won)} />
            <KpiCard
              label="Win Rate"
              value={overall?.win_rate != null ? `${overall.win_rate}%` : "—"}
            />
          </div>

          <div className="mb-5">
            <FunnelChart stages={data.stages} />
          </div>

          <ConversionTable stages={data.stages} />

          {data.breakdown?.length > 0 && (
            <div className="mt-5 rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 text-[13px] font-medium text-slate-700">
                Breakdown by {by}
              </div>
              <Table className="text-[13px]">
                <TableHeader className="bg-slate-50 text-left text-slate-500">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 font-medium text-slate-500">{by === "rep" ? "Rep" : by === "project" ? "Project" : "Source"}</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Entered</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Won</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Win Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.breakdown.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="py-2.5 text-slate-900">{row.label}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.entered}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.won}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.win_rate}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
